import { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { CodeField, RememberDevice } from '../auth/TwoFactorSteps';
import { portalAuthApi } from '../../services/portalApi';
import { getApiErrorMessage } from '../../lib/apiError';

// Portal second step: the 6-digit code we just emailed.
export function PortalCodeStep({ mfa, onVerified, onExpired }) {
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await portalAuthApi.verifyCode(mfa.mfaToken, code, remember);
      onVerified(data);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Verification failed');
      if (err.response?.status === 401 || err.response?.status === 423) onExpired(message);
      else { setError(message); setCode(''); }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await portalAuthApi.resendCode(mfa.mfaToken);
      toast.success('A new code is on its way');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send a new code'));
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="portal-code-step">
      <div className="text-center">
        <Mail className="w-6 h-6 mx-auto mb-2 text-gray-500" />
        <p className="text-sm text-gray-600 dark:text-gray-300">
          We've emailed a 6-digit code to <strong>{mfa.emailHint}</strong>. It expires in 10 minutes.
        </p>
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 text-center" data-testid="portal-code-error">{error}</p>}
      <CodeField value={code} onChange={setCode} disabled={busy} testId="portal-code-input" />
      <RememberDevice checked={remember} onChange={setRemember} testId="portal-remember-device" />
      <Button type="submit" className="w-full" disabled={code.length !== 6 || busy} data-testid="portal-code-verify">
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Verify and sign in
      </Button>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <button type="button" onClick={resend} disabled={resending} className="hover:text-gray-900 dark:hover:text-white" data-testid="portal-code-resend">
          {resending ? 'Sending…' : 'Send a new code'}
        </button>
        <button type="button" onClick={() => onExpired('')} className="hover:text-gray-900 dark:hover:text-white" data-testid="portal-code-back">Back</button>
      </div>
    </form>
  );
}
